/**
 * Order builder — state and rendering.
 *
 * Nothing here computes a price and nothing here touches the cart. Arithmetic
 * lives in rf-pricing.js and payload shaping in rf-order-payload.js, both pure
 * and tested under plain node; the cart calls arrive in rf-order-cart.js. This
 * file is the DOM: read the controls, ask those modules, write the result.
 *
 * THE DOM IS THE STATE. There is no state object. `readState()` reads the
 * controls fresh on every render, which is the pattern rf-album-calculator.js
 * established and the reason two instances on one page cannot desynchronise.
 *
 * FOUR THINGS THAT LOOK LIKE OVERSIGHTS AND ARE NOT:
 *
 * 1. IT NEVER READS /cart.js. Plan decision D19. State comes from ?albums= and
 *    the section's own defaults, never from the cart. Adding that read would give
 *    the configuration two sources of truth and owe an answer for which wins.
 *
 * 2. CLAMPING HAPPENS ON `change`, NEVER ON `input`. Clamping per keystroke is
 *    what makes a field impossible to type into: selecting "1" and typing "2"
 *    toward "25" rewrites to 2 and moves the caret, and backspacing to empty
 *    coerces to the floor, so a digit reappears and the field cannot be cleared.
 *    So `input` sanitises characters only and the field is allowed to hold "",
 *    "0" and "007"; `change` and blur commit. A separate committed value means an
 *    empty field does not flash the summary to zero.
 *    (This note used to open "with min=1", describing the album field, and then
 *    said "both counts floor at 0 as of 3 Sep 2026". Neither is a fact any more:
 *    the photo-set floor is 0 and the ALBUM floor is a merchant setting -- see
 *    THE ALBUM FLOOR IS A MERCHANT SETTING below -- so the example is stated
 *    without a floor value rather than with one that is true in one position.)
 *
 *    (Since U4 the photo-set field has a CEILING as well as a floor, and it is
 *    applied in the same place for the same reason -- see THE SETS CEILING
 *    below. Nothing was added to the `input` listener.)
 *
 * 3. THE LIVE REGION IS NOT THE SUMMARY PANEL. It is a separate visually-hidden
 *    one-sentence region, because wrapping the panel would make a screen reader
 *    re-announce every line on every stepper click. It is cleared by replacing
 *    textContent, never by toggling aria-hidden -- removing a live region from the
 *    accessibility tree and putting it back races the AT's own change detection.
 *
 * 4. IT ASSIGNS NO CSS CUSTOM PROPERTIES. Plan decision D11. docs/check-dead-vars.sh
 *    reads CSS and cannot see a setProperty call, so a property assigned only from
 *    JavaScript would be reported as dead -- a false positive on a check whose
 *    whole value is that it has no allowlist. State travels through classes and
 *    data- attributes instead.
 *
 * THE ALBUM FLOOR IS A MERCHANT SETTING, AND THAT IS THE WHOLE OF FEATURE 1 ON
 * THIS FILE. It was an unconditional zero from 3 Sep 2026 until ALBUM-MIN, and
 * this note said so; `allow_zero_album_orders` (Theme settings -> Order rules)
 * now decides it, and the two positions are not symmetrical. Set to ON, a
 * customer may send loose photos, or loose photos plus a USB, with no albums at
 * all. Left OFF -- the schema default, and the shipped position -- the count
 * floors at 1 here and a loose-photos-only order is refused at submit by
 * buildCartPayload() in rf-order-payload.js.
 *
 * THE SWITCH REACHES THIS FILE AS THE VARIANT MAP'S `allowZeroAlbums`, read once
 * in bind() as `map && map.allowZeroAlbums === true` and passed down; every
 * absence -- no stored setting, no key, no map at all -- lands on 1. The note at
 * that read is the long form.
 *
 * Every album count this file COMMITS therefore runs through
 * pricing.parseCount(..., albumFloor), never through pricing.clampAlbums, whose
 * floor of 1 is unconditional and is still correct for the shipped pricing page
 * and for the ?albums= handoff (see the note at that call site). isUnsellable()
 * below is the exception and deliberately so: it reads pricing.orderAlbums,
 * whose floor is 0, because a gate that cannot see a zero cannot fire on one.
 *
 * WHAT NO FLOOR CAN DO is stop the page QUOTING albums nobody is buying, so
 * display sites are gated too -- and NONE of those gates may be deleted as
 * unreachable, because the permissive position reaches every one of them. Two
 * on the album count itself -- the per-album rate row and the album summary row
 * -- and SEVEN more on the unsellable state below. Five of those share that
 * state with the self-serve ceiling and are written from `refusing`, the union
 * of the two: the due-today row, its note, both Step 4 amount lines and the
 * sticky mobile bar. Those five stay fully live while the minimum is on,
 * through `over`. The other two are gated on `unsellable` alone -- the order-
 * value row and the pay-in-full saving -- because an over-ceiling order still
 * has a list value to itemise and an empty one does not; those two, and the
 * minimum-order note, are the three that go dormant while enforcing.
 *
 * "UNSELLABLE" HAS ONE DEFINITION AND IT IS POSITION-INDEPENDENT:
 * albums === 0 && sets === 0. The USB does NOT count toward it -- a cart holding
 * only a USB is a $24.99 container for images nobody is sending us. (Keep that
 * expression on ONE line -- the grep below is a substring scan and a rewrap
 * hides it from the sweep that is meant to find it.) isUnsellable() below is
 * this file's statement of the rule, and buildCartPayload() in
 * rf-order-payload.js throws on the same condition. Two copies, because
 * rf-pricing.js is arithmetic and owns no notion of a sellable order; if you
 * change one, grep the other:
 *
 *   grep -rn '=== 0 && ' theme/assets/
 *
 * FIVE hits today, and the figure is asserted rather than remembered -- see
 * `ALBUM-MIN U5: the header's own grep instruction reports the count it measures`
 * in tests/order-builder-strings.test.js, because a count written into a comment
 * is the thing this repo has been wrong about most often. Three here: the
 * definition above, the grep line itself and isUnsellable() below. Two in
 * rf-order-payload.js.
 *
 * ⚠ THE FIFTH IS NOT A COPY OF THE RULE. It was four until ALBUM-MIN U2, and the
 * hit that unit added -- `albums === 0` with `!allowsZeroAlbums` beside it -- is
 * the ALBUM MINIMUM, a narrower and switch-dependent condition that is not this
 * one. The grep is a sweep, not a list of synonyms: read what each hit says
 * before assuming they agree.
 *
 * THE SETS CEILING IS A MERCHANT SETTING TOO, AND IT IS THE ONLY LIMIT ON THIS
 * PAGE THE CUSTOMER CANNOT TYPE PAST. `max_self_serve_sets` (Theme settings ->
 * Order rules) rides in the variant map, pricing.selfServeLimits() resolves it,
 * render() stashes the result on `el.limits`, and both photo-set entry paths --
 * the field's commit and the stepper -- clamp to it. A commit that had to clamp
 * reveals the message beside the field and is announced; the first commit at or
 * under the limit clears it again. capSets() is the whole rule.
 *
 * ⚠ THE ALBUM COUNT IS NOT CAPPED, AND THAT ASYMMETRY IS THE POINT (R1/R2). Past
 * its own limit an album order is offered a conversation, which is a route this
 * page would DELETE if it silently corrected the number instead. Photo sets have
 * no such route by decision, so they get a hard entry cap and a sentence.
 *
 * ⚠ AND THE CAP IS NOT AN ENFORCEMENT BOUNDARY. /cart/add.js and /cart/change.js
 * are Shopify-served and cannot be closed from a theme, so everything here is an
 * affordance; exceedsSelfServe() at submit is the last THEME-controlled refusal,
 * not a guarantee, and its sets branch stays live precisely because a DOM edit, a
 * stale tab and a hand-built payload all reach it without passing through
 * capSets(). Do not delete it as unreachable.
 *
 * This file only ever DISPLAYS that state. Nothing here refuses a submit: the
 * button stays live and the refusal happens in the cart path, which is where a
 * customer who typed 0 on their way to typing 3 expects nothing to happen.
 *
 * Requires rf-pricing.js and rf-order-payload.js to have loaded first, which the
 * ordering in layout/theme.liquid pins.
 */
(function () {
  'use strict';

  // Same escape hatch rf-order-cart.js uses, and for the same reason: the string
  // helpers below are pure, and a pure function reachable only through a headless
  // browser has a slow, flaky feedback loop for behaviour that deserves a fast
  // one. fill()'s whole justification -- that split/join survives a `$&` in the
  // value and fills a repeated token -- is exercised by no real template today,
  // so without an export nothing tests the reason it exists. No build step.
  var isCommonJS = typeof module === 'object' && module.exports;
  var pricing = isCommonJS ? require('./rf-pricing.js') : window.PhotokivePricing;

  /** How long to wait after the last change before announcing. */
  var ANNOUNCE_MS = 400;

  if (isCommonJS) {
    // The pure string helpers, plus announce(). The boot path is skipped outright
    // rather than stubbed, because everything else here reaches for `document`.
    //
    // announce() DOES touch the DOM and is exported anyway: its only argument is
    // the element map, it reads two of its keys and writes one, and it needs no
    // globals at all -- so a plain object with a `live` stub drives the real
    // function. It earns the hatch because its decision is not observable any
    // other way: the difference between clearing a live region and leaving a
    // stale price in it is invisible on screen, invisible to `theme check`, and
    // invisible to every harness that reads rendered HTML. Same argument
    // rf-order-cart.js's exports make.
    module.exports = {
      fill: fill,
      rateSaveNote: rateSaveNote,
      setsSummaryLabel: setsSummaryLabel,
      attrAllowBlank: attrAllowBlank,
      liveSentence: liveSentence,
      isUnsellable: isUnsellable,
      announce: announce
    };
    return;
  }

  function setup(root) {
    var scope = root || document;
    var list = [].slice.call(scope.querySelectorAll('[data-rf-ob-form]'));
    if (scope !== document && scope.matches && scope.matches('[data-rf-ob-form]')) {
      // The section root can BE the component when the theme editor reloads just
      // this section, in which case querySelectorAll on it would miss itself.
      list.push(scope);
    }
    list.forEach(bind);
  }

  function bind(form) {
    if (form.dataset.rfBound === 'true') return;
    form.dataset.rfBound = 'true';

    // One flat element map, resolved once. Queried from the form rather than from
    // document so two builders on one page could not cross-wire.
    var el = {
      albums: form.querySelector('[data-rf-ob-albums]'),
      albumsUnit: form.querySelector('[data-rf-ob-albums-unit]'),
      sets: form.querySelector('[data-rf-ob-sets]'),
      setsUnit: form.querySelector('[data-rf-ob-sets-unit]'),
      rate: form.querySelector('[data-rf-ob-rate]'),
      rateSave: form.querySelector('[data-rf-ob-rate-save]'),
      // THE ROW, not the figure -- same reason grandRow and the two amount lines
      // carry the hook. It states a PER-ALBUM PRICE, so on an order buying no
      // albums it quotes $129 for a product that is not in the cart. The hook has
      // been in the markup since the row was written; this file queried it
      // NOWHERE until 3 Sep 2026, which is how the rate row was the one
      // price-bearing element on the page with no gate of any kind.
      rateRow: form.querySelector('[data-rf-ob-rate-row]'),
      albumsTotal: form.querySelector('[data-rf-ob-albums-total]'),
      setsTotal: form.querySelector('[data-rf-ob-sets-total]'),
      depositNow: form.querySelector('[data-rf-ob-deposit-now]'),
      fullNow: form.querySelector('[data-rf-ob-full-now]'),
      depositNote: form.querySelector('[data-rf-ob-deposit-note]'),
      fullNote: form.querySelector('[data-rf-ob-full-note]'),
      // THE AMOUNT LINES, not their figures -- the same reason grandRow carries
      // the hook rather than grand. Blanking the figures alone would leave
      // "due today · later" standing beside nothing.
      depositAmt: form.querySelector('[data-rf-ob-deposit-amt]'),
      fullAmt: form.querySelector('[data-rf-ob-full-amt]'),
      // THE ROW as well as its two slots. It was the one summary row written
      // unconditionally -- sumSetsRow, sumUsbRow, sumListRow and sumSaveRow all
      // gate the row AND the write, and this one gated neither, because an album
      // count below 1 was unreachable until 3 Sep 2026.
      sumAlbumsRow: form.querySelector('[data-rf-ob-sum-albums-row]'),
      sumAlbumsLbl: form.querySelector('[data-rf-ob-sum-albums-lbl]'),
      sumAlbums: form.querySelector('[data-rf-ob-sum-albums]'),
      sumSetsRow: form.querySelector('[data-rf-ob-sum-sets-row]'),
      sumSetsLbl: form.querySelector('[data-rf-ob-sum-sets-lbl]'),
      sumSets: form.querySelector('[data-rf-ob-sum-sets]'),
      sumUsbRow: form.querySelector('[data-rf-ob-sum-usb-row]'),
      sumUsbLbl: form.querySelector('[data-rf-ob-sum-usb-lbl]'),
      sumUsb: form.querySelector('[data-rf-ob-sum-usb]'),
      sumListRow: form.querySelector('[data-rf-ob-sum-list-row]'),
      sumList: form.querySelector('[data-rf-ob-sum-list]'),
      sumSaveRow: form.querySelector('[data-rf-ob-sum-save-row]'),
      sumSave: form.querySelector('[data-rf-ob-sum-save]'),
      grandLbl: form.querySelector('[data-rf-ob-grand-lbl]'),
      grand: form.querySelector('[data-rf-ob-grand]'),
      // THE ROW, not the figure. It is what render() hides in the two states
      // where the page is not selling -- past the self-serve ceiling and with an
      // unparseable variant map -- because hiding the figure alone would leave
      // "DUE TODAY" standing beside nothing. Paired with `.rf-ob-grand[hidden]`
      // in sections/order-builder.liquid: the class carries `display: flex`, and
      // an author `display` beats the UA sheet's [hidden]{display:none} outright
      // (Standing Rule 7).
      grandRow: form.querySelector('[data-rf-ob-grand-row]'),
      duenote: form.querySelector('[data-rf-ob-duenote]'),
      // The over-ceiling card's own note. Its TEXT is the second fallback for the
      // live region: with `live_region_ceiling` blanked in the editor the
      // announcement borrows the sentence already on the screen rather than
      // going back to quoting a price for an order the page has refused. Read
      // here so liveSentence() stays pure (D17); the string is read off the node
      // at render time, not cached.
      ceilingNote: form.querySelector('[data-rf-ob-ceiling-note]'),
      // The summary card's own note for the unsellable state, and the exact
      // counterpart of ceilingNote above: revealed by render() where the
      // due-today row used to be, and its TEXT is the second fallback for the
      // live region when `live_region_minimum` is blanked in the editor. Read
      // here so liveSentence() stays pure (D17).
      minimumNote: form.querySelector('[data-rf-ob-minimum-note]'),
      live: form.querySelector('[data-rf-ob-live]'),
      submitWrap: form.querySelector('[data-rf-ob-submit-wrap]'),
      submit: form.querySelector('[data-rf-ob-submit]'),
      overCeiling: form.querySelector('[data-rf-ob-over-ceiling]'),
      // THE ENTRY CAP'S MESSAGE, and the span inside it that holds the copy.
      // Both are SERVER-RENDERED and merely toggled -- the plan's U4 settles
      // that, and the reason is `aria-describedby`: an element injected only
      // after a clamp cannot be referenced before one. The container is what
      // `hidden` goes on; the span is read for its TEXT, which is the live
      // region's sentence in this state -- exactly the arrangement ceilingNote
      // and minimumNote have above, and for the same reason (liveSentence()
      // stays pure, D17).
      //
      // BOTH ARE NULL WHEN THE MERCHANT BLANKS `sets_cap_note`. The Liquid
      // omits the whole paragraph then, matching `minimum_note`, so show() and
      // the text read below both no-op and the clamp still happens silently
      // rather than opening an empty box. That is the error path U4 names.
      setsCap: form.querySelector('[data-rf-ob-sets-cap]'),
      setsCapNote: form.querySelector('[data-rf-ob-sets-cap-note]'),
      nojs: form.querySelector('[data-rf-ob-nojs]'),
      // The running-total bar (D8). Both queried from the form, like everything
      // else here: the bar is the last child of .rf-ob-col, which is inside the
      // form, so two builders on one page cannot cross-wire. Absent when the
      // merchant has turned `show_mobile_bar` off, which is why every write to
      // them goes through text()/show() -- both no-op on null.
      bar: form.querySelector('.rf-ob-bar'),
      barDue: form.querySelector('[data-rf-ob-bar-due]'),
      // Scoped to the section, not the document. This line read
      // `document.querySelector(...)` until 23 Aug 2026, which contradicted the
      // comment above it: the section ships a preset, so a second instance is one
      // click in the theme editor, and both forms would have read the FIRST map --
      // quoting and charging another section's product. The document fallback keeps
      // a single-instance page working if the map ever moves outside the section.
      mapEl: (form.closest('.shopify-section') || document).querySelector('[data-rf-ob-variants]') ||
             document.querySelector('[data-rf-ob-variants]')
    };
    if (!el.albums || !el.sets) return;

    var map = readMap(el.mapEl);

    // ── THE ALBUM FLOOR, READ ONCE PER BIND (ALBUM-MIN U4) ────────────────
    //
    // `map && map.allowZeroAlbums === true` is the CANONICAL SPELLING of this
    // read and its other site is buildCartPayload() in rf-order-payload.js. The
    // two are pinned as one expression by `ALBUM-MIN U2: the switch read has ONE
    // spelling across every asset that makes it` in tests/order-builder.test.js
    // -- a guard the `guaranteeLine !== false` pair does not have.
    //
    // ⚠ IT IS READ HERE AND PASSED DOWN, not re-read in bindEvents. Three sinks
    // consume it -- the seed below, both field commits and the stepper -- and a
    // second read is a second answer the moment anything about the map changes
    // between them. bindEvents takes it as an argument for exactly that reason;
    // do not "tidy" it into a local read there.
    //
    // ⚠ THE POLARITY IS THE OPPOSITE OF guaranteeLine's, deliberately. Only an
    // explicit `true` unlocks zero, so all three absences -- no stored setting,
    // no key in the map, and no map at all -- floor at 1. The last is not
    // hypothetical: sections/order-builder.liquid renders the variants JSON
    // inside its `sellable` gate while the album field and this stepper render
    // outside it, so an unresolved product ships builder markup with no map, and
    // that page must enforce too.
    var albumFloor = (map && map.allowZeroAlbums === true) ? 0 : 1;

    // Committed values, kept separate from the fields' display strings so an
    // empty or half-typed field does not flash the summary to zero.
    //
    // parseCount(value, albumFloor), NOT clampAlbums and no longer orderAlbums:
    // the seed reads the field the server rendered from `albums_start`, and
    // `album_floor` in sections/order-builder.liquid clamps that same value with
    // the same floor. clampAlbums would seed 1 on a section a merchant
    // deliberately configured to start at none; orderAlbums would seed 0 on a
    // store whose minimum is on. parseCount is what the field's own commit uses,
    // so the seed and the first `change` cannot land on different numbers.
    var committed = {
      albums: pricing.parseCount(el.albums.value, albumFloor),
      sets: pricing.clampSets(el.sets.value)
    };

    // ── the ?albums= handoff ──────────────────────────────────────────────
    // Documented truth table in docs/homepage-sections.md -- abc -> 1, -5 -> 1,
    // and no ceiling here, so a large number reaches the over-ceiling state
    // rather than being silently capped into a sellable one.
    //
    // THE LITERAL 1 HERE IS DELIBERATE AND IS THE ONE FLOOR THE SWITCH DOES NOT
    // MOVE. It is correct in BOTH positions and for two different reasons, which
    // is why it is not `albumFloor`:
    //   * with the minimum ENFORCED it agrees with every other floor by accident,
    //     not by derivation, and writing `albumFloor` here would hide that;
    //   * with the minimum OFF it still refuses, because honouring `?albums=0`
    //     would BE a URL-reachable zero-album entry point, and the zero-album
    //     plan's Scope Boundaries exclude one: "No zero-album entry point on the
    //     pricing page, and therefore `?albums=` keeps its floor of 1 ... Only
    //     the stepper and the field reach 0."
    //     (docs/plans/2026-09-03-001-feat-zero-albums-and-guarantee-cart-line-plan.md)
    // The customer can still step down to 0 the moment the page has loaded --
    // while the minimum is off -- and what this refuses is a LINK that lands
    // them there.
    //
    // ⚠ `?albums=` ABSENT, AND `?albums=` PRESENT-BUT-EMPTY, BOTH FALL THROUGH
    // TO THE SEED. So this branch is not the only thing standing between a link
    // and a zero, and the seed's floor above is what answers those two.
    var inbound = new URLSearchParams(window.location.search).get('albums');
    if (inbound !== null && String(inbound).trim() !== '') {
      committed.albums = pricing.parseCount(inbound, 1);
      el.albums.value = committed.albums;
    }

    bindEvents(form, el, committed, albumFloor);
    render(form, el, committed, map, { announce: false });
  }

  function readMap(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      // A map we cannot parse is not a map. Returning null puts the page into the
      // same honest state as one with no products selected, rather than letting a
      // half-read object produce a plausible wrong price.
      return null;
    }
  }

  // `albumFloor` ARRIVES AS AN ARGUMENT, RESOLVED BY bind() FROM THE MAP. It is
  // not re-read here on purpose -- see the block at the read site. It reaches
  // both the field commit and the stepper below, which are the two ways a
  // customer can move the album count, and the two floors have to be the same
  // number for `drifted()`/`commit()` to be idempotent on a field the stepper
  // just wrote.
  function bindEvents(form, el, committed, albumFloor) {
    // Per-field commit hooks, filled by the loop below. The two number fields'
    // commit() is a closure over `key`, `input` and `floor`, so the only honest
    // way to reach it from rfObState is for the loop to hand out a reference --
    // hence this array rather than a module-level or window-level function.
    // Declared before rfObState because that function closes over it; it is only
    // ever READ after bind() has finished filling it.
    var fields = [];

    // Publish the committed state so rf-order-cart.js does not have to re-read the
    // raw fields. Those two readers could diverge: this file deliberately lets a
    // field hold uncommitted text (typing "12" must not clamp to "1" mid-keystroke),
    // so a submit that never triggered blur -- macOS Safari does not focus a button
    // on click -- could build a payload for a count the summary never showed. R3
    // says the figure displayed must be the figure charged; this is what keeps the
    // two readers honest. Attached to the form so it survives a rebind.
    //
    // AND IT COMMITS FIRST. Publishing `committed` alone was still one event
    // ordering short of the guarantee: `change` and `blur` are the only things
    // that commit, and neither is guaranteed to have fired by the time a submit
    // handler runs. Two real orderings reach here with a typed-but-uncommitted
    // field:
    //   * macOS Safari does not focus a button on click, so clicking the submit
    //     never blurs the field.
    //   * rf-order-cart.js's setBusy() sets `disabled` on the field before it
    //     reads this function, and disabling the focused element moves focus in
    //     Chrome WITHOUT firing blur.
    // So the field is committed here, synchronously, before the state is read --
    // which also re-renders, so the summary and the bar agree with the payload
    // rather than being one keystroke behind it.
    form.rfObState = function () {
      commitPending();
      return readState(form, el, committed);
    };

    // Focus is checked first because it is the ordinary case; `drifted` covers
    // the case where the platform has already moved focus away without a blur,
    // which is the one the comment above is really about. commit() is idempotent
    // on a field that already agrees with `committed`, and drifted() is exactly
    // the test for that -- so nothing renders when nothing changed.
    function commitPending() {
      fields.forEach(function (f) {
        if (f.input === document.activeElement || f.drifted()) f.commit();
      });
    }

    var mapEl = el.mapEl;
    var map = readMap(mapEl);
    var timer = null;

    function commitAndRender(opts) {
      render(form, el, committed, map, opts || { announce: true });
    }

    /**
     * Apply the photo-set ceiling to a value that is about to be COMMITTED, and
     * record on `el` whether it had to move.
     *
     * ONE EXPRESSION FOR BOTH ENTRY PATHS -- the field's change/blur/Enter and
     * the stepper -- because two copies of "is this over the limit" is how the
     * field and the buttons come to disagree about where the limit is. The long
     * note above the field loop is the reasoning; this is where it lives.
     *
     * ⚠ `el.setsClamped` IS THE MESSAGE'S WHOLE LIFECYCLE, and it is a flag
     * rather than something render() can derive, because AT the limit a clamped
     * commit and a customer who typed the limit exactly are the same number.
     * Exactly the limit must show nothing -- it is the off-by-one that matters
     * most -- so the difference has to be remembered at the moment it happened.
     * Set true by the commit that clamps, false by the first commit that MOVES
     * the count to at or under the limit; nothing else writes it, so there is no
     * timer and no dismiss control. A message the customer can close without
     * acting is a silent clamp with extra steps.
     *
     * ⛔ AND A COMMIT THAT CHANGES NOTHING CLEARS NOTHING -- `before` is the
     * whole reason this takes two arguments. `change` is not the only thing that
     * commits: blur does, Enter does, and rfObState() does on its way to the
     * cart. Every one of those re-runs the closure over a field that already
     * holds the clamped value, so a rule written as "any commit at or under the
     * limit clears it" would dismiss the message the moment the customer tapped
     * away -- which is a silent clamp with one extra frame. The stepper's arm
     * expresses the same rule by sitting after its no-change return.
     *
     * A boolean on `el` for the same reason `el.pending` is one: `el` is this
     * form's per-bind scratch, resolved once in bind() and handed to both
     * bindEvents() and render(), and it is the only object those two share. A
     * rebind builds a fresh one, so a section reload starts with no message.
     */
    function capSets(parsed, before) {
      var ceiling = el.limits.sets;
      var next = parsed > ceiling ? ceiling : parsed;
      if (next !== parsed) el.setsClamped = true;
      else if (next !== before) el.setsClamped = false;
      return next;
    }

    // ── the two number fields ────────────────────────────────────────────
    // THE TWO FLOORS ARE NO LONGER THE SAME NUMBER. Sets always floor at 0;
    // albums floor at `albumFloor`, which is 0 only while the merchant has
    // allowed zero-album orders. Both were 0 from 3 Sep 2026 to ALBUM-MIN U4.
    //
    // ⚠ THIS IS A SUBMIT-TIME FLOOR AS WELL AS A UI ONE, and it is the reason
    // the album arm may not be left at 0 "because the payload backstop catches
    // it". rfObState() above calls commitPending(), which re-runs this closure,
    // so the count buildCartPayload() receives has already been through here.
    // The backstop in rf-order-payload.js is what catches the paths that never
    // reach a field at all -- readState()'s never-booted default, and a payload
    // built by anything but this form.
    //
    // The album floor was 1 until 3 Sep 2026, and lowering it is what stopped a
    // typed "0" -- and a pasted "abc", which parseCount sends to the floor --
    // becoming one album at $129 the customer never asked for. That failure is
    // still the reason the floor is a variable rather than a constant: while the
    // minimum is OFF the typed 0 must stay 0.
    // ── THE SETS CEILING (U4) ────────────────────────────────────────────
    //
    // ⛔ AT COMMIT, NEVER IN THE `input` LISTENER. The plan's D1 says why in one
    // line: that listener truncates at the first non-digit and MUST NOT delete,
    // because deleting turned "1.5" into "15". A write-back clamp there would be
    // the same class of bug one function lower down -- selecting "100" and
    // typing "4" toward "400" would rewrite to the limit and move the caret, and
    // the field would be impossible to type a large number into even to correct
    // it. The `input` listener below is unchanged; everything here runs on
    // `change`, blur, Enter and a stepper press.
    //
    // `max=` ON THE INPUT IS NOT THIS. The form carries `novalidate`, so `max`
    // drives the native spinner and the announced range and blocks nothing at
    // all -- see the block above the form tag in sections/order-builder.liquid.
    // A typed or pasted 400 arrives here untouched. THIS is the cap the customer
    // meets.
    //
    // AND IT IS NOT THE LAST WORD EITHER. pricing.exceedsSelfServe() still runs
    // at submit, in rf-order-payload.js and rf-order-cart.js, and its SETS
    // branch is not dead code now that the field cannot reach it: a DOM edit, a
    // tab left open across a limit change, and a payload built by anything but
    // this form all get there without passing through this function.
    //
    // THE CEILING IS READ OFF `el.limits`, WHICH render() STASHES. It is the
    // SAME object render() hid the submit and announced from -- resolved once
    // per render by pricing.selfServeLimits(map) and never re-read here, for
    // exactly the reason `albumFloor` is passed down rather than re-read: two
    // reads of a merchant setting are two chances to answer differently, and a
    // field that clamped at one number while the summary judged another is the
    // "figure displayed is not the figure charged" state this file exists to
    // prevent. bind() renders before any event can fire, so the stash is always
    // there by the time a commit runs.
    //
    // ⚠ ONLY THE SETS ARM HAS ONE. R2: 51 albums still routes to the quote card,
    // and capping the album field would DELETE that route rather than enforce
    // it -- the customer would be silently corrected to 50 and never offered the
    // conversation. The two arms differ for a stated reason, which is why this
    // is a branch and not a shared constant.
    [['albums', el.albums, albumFloor, false], ['sets', el.sets, 0, true]].forEach(function (pair) {
      var key = pair[0];
      var input = pair[1];
      var floor = pair[2];
      var capped = pair[3];

      // `input`: characters only. No clamping, no coercion, no write-back of a
      // different number -- see note 2 in the header.
      input.addEventListener('input', function () {
        // TRUNCATE at the first non-digit; do not delete it. Deleting turned "1.5"
        // into "15" while parseCount -- the function that actually commits -- reads
        // "1.5" as 1. A factor of ten apart on the same keystroke, and "2.5" became
        // 25, crossing the 20-album flat rate. Separators are stripped to match
        // parseCount, which also ignores spaces, commas and underscores.
        var cleaned = input.value.replace(/[\s,_]/g, '').replace(/^([0-9]*).*$/, '$1');
        if (cleaned !== input.value) input.value = cleaned;
      });

      // `change` and blur: commit. This is where clamping belongs.
      //
      // THE PARSE IS UNCHANGED AND STILL RUNS FIRST, which is what keeps a
      // pasted "1,000" worth 1000 rather than 1: parseCount() strips the
      // separators, and only then is the ceiling applied. Clamping a
      // MIS-PARSED number would cap the customer at the limit for asking for
      // ten.
      function commit() {
        var parsed = pricing.parseCount(input.value, floor);
        committed[key] = capped ? capSets(parsed, committed[key]) : parsed;
        input.value = committed[key];
        commitAndRender();
      }
      input.addEventListener('change', commit);
      input.addEventListener('blur', commit);

      // The hook rfObState commits through. `drifted` asks the only question
      // that matters: does the FIELD still show what `committed` holds? It
      // compares both the parsed value and the raw string, because "007" and
      // "" both parse to a committed number while displaying something else.
      fields.push({
        input: input,
        drifted: function () {
          return pricing.parseCount(input.value, floor) !== committed[key] ||
                 String(input.value) !== String(committed[key]);
        },
        commit: commit
      });

      // Enter in a number field would submit the form. Commit instead, so the
      // value is settled before Unit 9's submit handler ever sees it.
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      });
    });

    // ── the stepper buttons ──────────────────────────────────────────────
    // A button press IS a commit, so it clamps immediately. It fires only when
    // the value actually moved, so a press at the floor does not announce a
    // no-op to a screen reader.
    [].slice.call(form.querySelectorAll('[data-rf-ob-inc], [data-rf-ob-dec]'))
      .forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-rf-ob-inc') || btn.getAttribute('data-rf-ob-dec');
          var up = btn.hasAttribute('data-rf-ob-inc');
          // THE BRANCH IS BACK, AND ITS ARMS DIFFER AGAIN. This read
          // `key === 'albums' ? 1 : 0` until 3 Sep 2026, then `var floor = 0;`
          // with a note calling the branch dead code -- correct at the time,
          // because both counts floored at 0 and a branch whose arms are
          // provably identical reads like a rule that is not there. ALBUM-MIN U4
          // makes them differ whenever the merchant has NOT allowed zero-album
          // orders, so the two arms are a real rule once more.
          //
          // `albumFloor`, NOT a literal 1: the two arms have to agree with the
          // field's commit above, which is the same closure a stepper press
          // leaves the field in. The stepper is one of the only two ways to
          // reach 0 albums at all, and only while the minimum is off (the field
          // is the other -- `?albums=` deliberately is not, in either position).
          var floor = key === 'albums' ? albumFloor : 0;
          // THE CEILING BESIDE THE FLOOR (U4), AND ITS ARMS DIFFER FOR A STATED
          // REASON TOO. `null` for albums is not "not written yet": R2 keeps the
          // album quote route, and a cap here would silently correct 51 to 50
          // and never offer the conversation. Do not collapse this into the
          // floor's branch and do not give the album arm a number -- the floor
          // branch above was once deleted as dead code when its two arms agreed,
          // and had to be restored.
          var ceiling = key === 'albums' ? null : el.limits.sets;
          var input = key === 'albums' ? el.albums : el.sets;
          var before = committed[key];
          var next = pricing.parseCount(before + (up ? 1 : -1), floor);
          if (ceiling !== null && next > ceiling) next = ceiling;
          // AND THE EXISTING NO-OP IS WHAT GIVES `+` ITS BEHAVIOUR AT THE CAP,
          // for free: at the limit the press resolves to the limit, nothing
          // moved, and the handler returns before it renders. That is also why
          // pressing `+` at the cap shows NO message and cannot re-fire one
          // already on screen -- the customer did not overreach, the control
          // refused to move. ⚠ THE ORDER OF THESE TWO LINES IS LOAD-BEARING: a
          // press that changes nothing must not be able to arm the message,
          // because nothing renders after it and the message would then appear
          // on some later, unrelated render.
          if (next === before) return;
          committed[key] = next;
          input.value = next;
          // A PRESS THAT MOVED THE COUNT IS A COMMIT AT OR UNDER THE CEILING --
          // the clamp two lines up guarantees it -- so it CLEARS the message,
          // which is the lifecycle's second half. It never RAISES one: the only
          // value a press can ask for above the ceiling is one more than a count
          // already at it, and that press is the no-op above. Anything else
          // needs a count already past the limit, which the field can no longer
          // produce and which belongs to the submit-time backstop, not to a
          // message about what the customer just typed.
          if (ceiling !== null) el.setsClamped = false;
          commitAndRender();
        });
      });

    // ── the radio groups ─────────────────────────────────────────────────
    // Real radios, so `change` is all that is needed -- the browser handles
    // arrow keys, grouping, and the checked state.
    [].slice.call(form.querySelectorAll('[data-rf-ob-usb], [data-rf-ob-mode]'))
      .forEach(function (input) {
        input.addEventListener('change', function () {
          syncChecked(form);
          commitAndRender();
        });
      });

    // AT BIND TOO, not only on change. The section ships with one option checked
    // in each group, and a browser without :has() would otherwise show no
    // selection at all until the customer touched something -- on the step that
    // chooses how they pay.
    syncChecked(form);

    // ── the submit ───────────────────────────────────────────────────────
    // rf-order-cart.js owns what happens on submit. This listener stays as the
    // guard against a NATIVE post: the form has no action, so an unprevented
    // submit would POST to this URL and reload the page. Both listeners fire;
    // this one only ever prevents.
    form.addEventListener('submit', function (e) {
      e.preventDefault();
    });

    // Announcements are debounced separately from rendering: the visible summary
    // updates synchronously so the figure never lags a click, while the spoken
    // sentence waits for the interaction to settle.
    form.addEventListener('rf-ob:changed', function () {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(function () { announce(el); }, ANNOUNCE_MS);
    });
  }

  function readState(form, el, committed) {
    var mode = form.querySelector('[data-rf-ob-mode]:checked');
    var usb = form.querySelector('[data-rf-ob-usb]:checked');
    return {
      albums: committed.albums,
      sets: committed.sets,
      usb: !!(usb && usb.value === 'yes'),
      mode: mode ? mode.value : 'deposit'
    };
  }

  function text(node, value) {
    if (node) node.textContent = value;
  }

  function show(node, on) {
    if (!node) return;
    if (on) node.removeAttribute('hidden');
    else node.setAttribute('hidden', '');
  }

  /**
   * Mirror each radio's checked state onto its `.rf-ob-choice` label as
   * `is-checked` (D10).
   *
   * WHY, and it is not decoration: the selected payment option is drawn by
   * `.rf-ob-choice:has(input:checked)` in sections/order-builder.liquid, and
   * `:has()` is absent on older Android WebViews and Samsung Internet -- so on
   * those browsers the only visible difference between "pay a deposit" and "pay
   * in full" is a 20px dot that also does not paint, because its rules are
   * `:has()` too. Three rules, one state, and it is on the page that takes money.
   *
   * The class is written for EVERY radio in both groups on every call, so the
   * unchecked siblings are cleared in the same pass -- a browser's own
   * radio-group exclusivity is not something this function has to model, it just
   * has to read it. Called at bind (so the shipped default selection is visible
   * at first paint) and on every `change`.
   *
   * classList, not className: the labels carry no other class today, but a
   * string assignment would silently delete one the day they do.
   */
  function syncChecked(form) {
    [].slice.call(form.querySelectorAll('[data-rf-ob-usb], [data-rf-ob-mode]'))
      .forEach(function (input) {
        var label = input.closest ? input.closest('.rf-ob-choice') : null;
        if (!label) return;
        if (input.checked) label.classList.add('is-checked');
        else label.classList.remove('is-checked');
      });
  }

  function render(form, el, committed, map, opts) {
    var st = readState(form, el, committed);
    var m = pricing.moneyFromCents;

    // Units follow the number, so 0 reads "sets" and 1 reads "set".
    var sec = form;
    text(el.albumsUnit, unitFor(sec, 'albums', st.albums));
    text(el.setsUnit, unitFor(sec, 'sets', st.sets));

    // THE TWO CEILINGS THIS PAGE IS JUDGED AGAINST, RESOLVED ONCE PER RENDER AND
    // PASSED DOWN. `map` is this render's argument, so the value cannot drift
    // between the submit gate below and the live region at the foot of this
    // function -- and those two agreeing is the whole reason liveSentence() reads
    // the ceiling through the same call rather than through a second one.
    // pricing.selfServeLimits() owns the `map &&` guard and the junk policy; do
    // not re-read the keys here.
    //
    // ⚠ IT SITS ABOVE THE `!map` RETURN, AND THAT IS DELIBERATE (U4). Both
    // number fields render OUTSIDE the section's `sellable` gate while the
    // variant map renders inside it, so a page with unresolved products ships a
    // live photo-set field and no map -- and that field still has to cap. The
    // resolver answers `null` with the shipped fallbacks, which is the only
    // answer available to a page that was told nothing, and the same one every
    // other reader in this theme gets there.
    //
    // THE STASH IS HOW THE COMMIT PATH SEES IT. bindEvents() clamps against
    // `el.limits` rather than resolving a second object of its own -- see
    // capSets() -- so the number the field enforces and the number the submit
    // gate judges are the SAME object, not two reads that happen to agree.
    var limits = pricing.selfServeLimits(map);
    el.limits = limits;

    // THE ENTRY CAP'S MESSAGE, WRITTEN FROM THE FLAG capSets() KEEPS.
    //
    // ⚠ ABOVE THE `!map` RETURN TOO, and for a different reason from `limits`.
    // Everything that branch puts away -- the due-today row, both amount lines,
    // the minimum-order note -- lives in the SUMMARY RAIL and quotes a figure
    // the page can no longer produce; "one message per state" is a rule about
    // that card. This message is not on that card. It sits beside the field it
    // changed, in Step 2, which renders identically in both branches, and it
    // explains a number that moved under the customer's hands. A clamp the page
    // does not own up to is the silent clamp D2 refuses, whether or not the
    // page can price the result.
    //
    // NO BLANKING BESIDE THE TOGGLE, unlike every conditional row on the summary
    // card. Those hold COMPUTED figures and can be rewritten from state; this
    // holds MERCHANT COPY the server rendered once, and there is nothing to
    // write it back from. `.rf-ob-capnote[hidden]` in
    // sections/order-builder.liquid is what carries the weight instead -- see
    // the note there, and `.rf-ob-line[hidden]` for the -$522 this rule class
    // exists because of.
    //
    // ── PRECEDENCE AGAINST THE OVER-CEILING CARD, DECIDED (U4) ───────────
    //
    // 101 sets AND 51 albums is reachable by typing, so this had to be decided
    // rather than discovered. ⚠ AND IT IS NOT THE PAIR IT LOOKS LIKE: with this
    // cap in place the field cannot commit a sets count above the limit at all,
    // so the breach in that state is the ALBUM one -- selfServeBreach() tests
    // albums first and would say so. What is genuinely on screen together is
    // this message (the photo-set count was corrected) and the card (the ALBUM
    // count needs a conversation).
    //
    // THE DECISION: BOTH RENDER, and this toggle is deliberately independent of
    // `over`. Three reasons, in order of weight:
    //
    //   1. They are about two different counts. Suppressing this one would make
    //      the photo-set clamp silent in a state a customer reaches by typing,
    //      and being announced is the ONLY thing that makes the clamp permitted
    //      at all (D2).
    //   2. They are not on one card. The file's "two refusals on one card would
    //      read as two problems" rule -- the one that keeps minimumNote away
    //      from the ceiling state -- is about the summary rail, where both of
    //      those live. This sits in Step 2, beside the field it changed.
    //   3. Neither is a refusal of the other. The card withdraws the sale; this
    //      explains an edit to an order the page would otherwise still sell.
    //
    // THE LIVE REGION CANNOT DO THAT -- it is one sentence -- so it ranks them
    // instead, and liveSentence() below states the order and why.
    //
    // ── THE MESSAGE'S NUMBERS ARE FILLED, NOT TYPED (U4 follow-up) ───────
    //
    // ⛔ A NUMBER WRITTEN INTO THE COPY IS A SECOND SOURCE FOR THE LIMIT, and
    // the merchant who moves the limit is exactly the merchant who will forget
    // the sentence: drop `max_self_serve_sets` to 25 and a hardcoded default
    // goes on telling the customer the cap is 100 while the field clamps at 25.
    // That is the `$118per album` seam -- a default carrying a value the page
    // no longer honours -- and Kurtis ruled it out rather than documented.
    //
    // {count} and {photos} are filled through fill(), the theme's one
    // substitution helper, from `limits` -- THE SAME OBJECT the submit gate and
    // the live region are judged with, resolved once at the top of this render.
    // A second selfServeLimits() call here would be a second chance to answer
    // differently, and the number in the sentence would then be free to
    // disagree with the number the field enforces.
    //
    // {photos} IS DERIVED, NEVER STORED: the limit times the set size, which
    // pricing.LOOSE_SET_PHOTOS now names. It is separated with
    // toLocaleString('en-US'), the same call money() and moneyFromCents() make
    // -- "10000 loose photos" reads as a typo.
    //
    // ⚠ THE TEMPLATE IS CACHED ON FIRST USE, because this writes back over the
    // node it read. Without the cache the second render would fill an
    // already-filled string, the placeholders would be gone, and a limit that
    // changed between renders could never be re-stated. `el` is the per-bind
    // scratch, so a rebind re-reads the server's copy.
    //
    // ⚠ NO PLACEHOLDER IS REQUIRED. fill() replaces what it finds and leaves
    // what it does not, so a merchant who writes a sentence with no {count} in
    // it gets that sentence -- the theme does not make its own placeholders
    // mandatory anywhere else either.
    if (el.setsCapNote) {
      if (el.setsCapTpl == null) el.setsCapTpl = String(el.setsCapNote.textContent);
      el.setsCapNote.textContent = fill(el.setsCapTpl, {
        '{count}': String(limits.sets),
        '{photos}': (limits.sets * pricing.LOOSE_SET_PHOTOS).toLocaleString('en-US')
      });
    }
    show(el.setsCap, el.setsClamped === true);

    // A map that is absent or unparseable means the page cannot quote a price.
    // Leave the CATALOGUE figures -- the rate row and the summary lines -- exactly
    // as the server rendered them rather than writing zeroes over correct markup,
    // put away the two slots that state what the customer would PAY (below, and
    // see why there), and route to contact.
    if (!map) {
      // HIDE THE SUBMIT. Until 23 Aug 2026 this hid the contact route and returned
      // before `show(el.submitWrap, ...)` ever ran, so an unparseable map left a
      // live submit button sitting over the server-rendered figures for
      // `albums_start` while every stepper silently changed nothing. The comment
      // above already said "route to contact"; the code did the opposite.
      show(el.submitWrap, false);
      show(el.overCeiling, true);
      // AND THE BAR GOES WITH IT. Same rule as the over-ceiling case below: the
      // bar exists to quote what is due, and this branch is the state where the
      // page cannot quote anything. Leaving it up would put a server-rendered
      // figure under a card that has just routed the customer to a conversation.
      show(el.bar, false);
      // AND THE CARD'S OWN TOTAL, for the same reason and by the same mechanism.
      // Until 28 Aug 2026 this branch returned here, before the two writes at the
      // foot of render() -- so a stepper move updated the unit noun beside the
      // field (written above) while the summary kept the SERVER-RENDERED total
      // for a different count. A page that has just said it cannot price this
      // order was quoting $300 beside a count of 2. Measured by
      // docs/interaction-test.sh, which plants a broken JSON island and steps.
      //
      // Hidden AND blanked, the rule every conditional row on this card carries:
      // a row that is merely hidden still holds its text, and hiding is one CSS
      // rule away from failing. There is nothing honest to write instead -- this
      // is the branch where no figure exists -- so the slots go empty rather
      // than to a stale number.
      // (This said "the four conditional summary rows" until 3 Sep 2026. The
      // album row joined them when the album floor went to 0, so the count was
      // about to be wrong; the rule is named without one now.)
      show(el.grandRow, false);
      show(el.duenote, false);
      text(el.grand, '');
      text(el.duenote, '');
      // AND STEP 4's TWO AMOUNT LINES. Added 31 Aug 2026 by ce:review, which found
      // all four card slots written UNCONDITIONALLY at the foot of render() --
      // downstream of this very return -- so in this branch they kept the figures
      // Liquid rendered for the page's STARTING album count while the summary
      // correctly blanked. The page hid its submit button, routed the customer to
      // a contact form, and went on quoting "$X due today · $Y later" for the
      // order it had just refused. Same class as the -$522 saving row, and the
      // reason this branch exists at all.
      show(el.depositAmt, false);
      show(el.fullAmt, false);
      text(el.depositNow, '');
      text(el.fullNow, '');
      text(el.depositNote, '');
      text(el.fullNote, '');
      // AND THE MINIMUM-ORDER NOTE STAYS AWAY, even at zero albums. This branch
      // already offers the customer one thing -- a conversation -- and "you need
      // at least one album or one set" is advice about a form the page has just
      // stopped being able to price. One message per state.
      show(el.minimumNote, false);
      return;
    }

    // THE SAME `limits` RESOLVED AT THE TOP OF THIS FUNCTION, not a second read.
    var over = pricing.exceedsSelfServe(st, limits);
    // THE OTHER STATE IN WHICH THIS PAGE IS NOT SELLING, and it is new on
    // 3 Sep 2026: nothing in the order. Every slot that quotes what the customer
    // would PAY is gated on `refusing`, the union of the two, so the whole card
    // cannot half-agree with itself. "Due today $0" beside a submit that will
    // refuse the order is the same defect the ceiling gating exists for, one
    // dollar figure lower -- and it really is "$0", not "$0.00", because
    // moneyFromCents prints whole dollars bare, which reads like a free order
    // rather than like a missing figure.
    //
    // The submit button and the over-ceiling card are gated on `over` ALONE and
    // deliberately so: an empty order is refused AT SUBMIT (R2), by
    // buildCartPayload() in rf-order-payload.js, not by hiding the button. A
    // customer who has stepped albums to 0 on their way to typing 3 has not
    // asked for the contact form.
    var unsellable = isUnsellable(st);
    var refusing = over || unsellable;
    show(el.overCeiling, over);
    show(el.submitWrap, !over);
    // THE BAR IS THE INVERSE OF THE SWAP, and it is written from the same
    // `refusing` as every other slot on this card that quotes a figure, so no
    // two of them can disagree about whether the page is selling (D8).
    // Past the ceiling the summary card stops offering a sale and offers a
    // conversation; a bar still quoting a price beside it would be two messages
    // on one small screen, and the price is the one the page has just refused to
    // honour. It read `!over` alone until 3 Sep 2026, so at zero albums it said
    // "Due today $0 -- Review order" on the phone layer while the card above
    // it was refusing the order. Back under the ceiling, and with something in
    // the order, it returns.
    show(el.bar, !refusing);
    // AND THE CARD'S OWN DUE-TODAY ROW, which D8 left behind. The bar was gated
    // on `over` and the row beside it was not, so past the ceiling the summary
    // card offered a conversation while still reading "Due today $7,900" with a
    // balance schedule under it. The comment above calls a bar quoting a price
    // beside a refusing card "two messages on one small screen"; it was two
    // messages on one CARD as well, at every width, and the rail's copy is the
    // one a desktop customer reads. Found by adversarial review, 28 Aug 2026.
    //
    // The ROW carries the hook, so the label goes with the figure. The writes are
    // gated below as well as the rows -- same rule as the summary lines.
    show(el.grandRow, !refusing);
    show(el.duenote, !refusing);
    // AND THE NOTE THAT TAKES THEIR PLACE. Only in the unsellable state, never
    // past the ceiling: that state has its own card, its own copy and its own
    // route out, and two refusals on one card would read as two problems.
    show(el.minimumNote, unsellable);

    var listCents = pricing.listSubtotalCents(st);
    var todayCents = pricing.dueTodayCents(st, st.mode);
    var balanceCents = pricing.balanceCents(st);
    var rateCents = pricing.albumListRateCents(st.albums);
    var isDeposit = st.mode === 'deposit';

    // ── the per-album rate row ───────────────────────────────────────────
    // GATED ON THE ALBUM COUNT, and this is the site the album floor of 0 makes
    // dangerous rather than merely untidy. albumListRateCents() stays on
    // clampAlbums's floor of 1 by design (rf-pricing.js says so at its
    // declaration), so at zero albums it returns the SINGLE-ALBUM rate: the row
    // would read "$129 per album · standard rate" beside "$0" on an order buying
    // no albums, quoting a price for a product that is not in the cart.
    //
    // Hidden AND blanked, the rule every conditional row on this page carries: a
    // row that is merely hidden still holds its text, and hiding is one CSS rule
    // away from failing -- which is precisely how -$522 reached the screen in
    // August. Paired with `.rf-ob-rate[hidden]` in sections/order-builder.liquid,
    // because `.rf-ob-rate` carries `display: flex` and an author `display` beats
    // the UA sheet's [hidden]{display:none} by cascade origin (Standing Rule 7).
    var buyingAlbums = st.albums > 0;
    show(el.rateRow, buyingAlbums);
    text(el.rate, buyingAlbums ? m(rateCents) : '');
    text(el.rateSave, buyingAlbums ? rateSaveNote(sec, st.albums) : '');
    text(el.albumsTotal, buyingAlbums ? m(rateCents * st.albums) : '');
    text(el.setsTotal, m(pricing.LOOSE_SET_CENTS * st.sets));

    // GATED ON `refusing`, like grandRow/duenote/bar above. Past the self-serve
    // ceiling the page says "this needs a custom quote" and hides submit, so a
    // live price on either payment card contradicts it; with nothing in the order
    // both cards would offer to charge $0 today for it. The album field
    // carries no `max` and now floors at 0, so a visitor reaches both states by
    // typing.
    show(el.depositAmt, !refusing);
    show(el.fullAmt, !refusing);
    text(el.depositNow, refusing ? '' : m(pricing.dueTodayCents(st, 'deposit')));
    text(el.fullNow, refusing ? '' : m(pricing.dueTodayCents(st, 'full')));

    // E6. Each payment card states BOTH of its own figures, so the two options
    // can be compared at the moment of choosing -- which is where the comp puts
    // this, not in the summary.
    //
    // NOTE THE LITERAL MODES ABOVE, AND THE ABSENCE OF `isDeposit` BELOW. These
    // four writes describe the two OPTIONS, not the selected one: the deposit
    // card shows the deposit's figures whichever radio is checked, exactly as
    // `dueTodayCents(st, 'deposit')` does one line up. Gating the balance on
    // `isDeposit` -- which is the correct condition for a SUMMARY element, and
    // is what the plan specified -- would blank this card's balance the instant a
    // customer clicked Pay in full, and the comparison is the whole point.
    //
    // `balanceCents` is `listSubtotal - chargedCents(st,'deposit')`, so it is
    // mode-independent and returns a real figure on both paths. That is why the
    // pay-in-full note is filled with the EMPTY STRING rather than left unfilled:
    // both notes are merchant-editable, and a merchant who types {balance} into
    // the pay-in-full one must not be able to announce a dollar balance the
    // customer does not owe. summarySentence() carries the same guard and says
    // in as many words that it has to be explicit.
    text(el.depositNote, refusing ? '' :
         fill(attr(sec, 'data-rf-ob-deposit-note-tpl', 'due today · {balance} later'),
              { '{balance}': m(balanceCents) }));
    text(el.fullNote, refusing ? '' :
         fill(attr(sec, 'data-rf-ob-full-note-tpl', 'due today · nothing later'),
              { '{balance}': '' }));

    // THE ALBUM SUMMARY ROW, gated at last. It was the only unconditional row on
    // this card: sumSetsRow, sumUsbRow, sumListRow and sumSaveRow have always
    // gated the row AND the write, and this one gated neither, because an album
    // count of 0 was unreachable before 3 Sep 2026. Left ungated it reads
    // "0 albums — $0" on an order with no albums in it, which is a line item for
    // nothing, above a subtotal that correctly omits it.
    //
    // GATE THE WRITE, NOT ONLY THE ROW -- the rule stated at every other gated
    // slot in this file, and in the `.rf-ob-line[hidden]` comment in
    // sections/order-builder.liquid: a row that is merely hidden still holds its
    // text, and hiding is one CSS rule away from failing.
    show(el.sumAlbumsRow, buyingAlbums);
    text(el.sumAlbumsLbl, buyingAlbums ? st.albums + ' ' + unitFor(sec, 'albums', st.albums) : '');
    text(el.sumAlbums, buyingAlbums ? m(rateCents * st.albums) : '');

    // Same rule as the saving below: gate the write, not only the row. A hidden
    // row still holds its text, so an unwritten one cannot leak a figure that
    // belongs to a configuration the customer is not buying.
    show(el.sumSetsRow, st.sets > 0);
    text(el.sumSetsLbl, st.sets > 0 ? setsSummaryLabel(sec, st.sets) : '');
    text(el.sumSets, st.sets > 0 ? m(pricing.LOOSE_SET_CENTS * st.sets) : '');

    show(el.sumUsbRow, st.usb);
    text(el.sumUsbLbl, st.usb ? attr(sec, 'data-rf-ob-usb-label', 'USB Drive') : '');
    text(el.sumUsb, st.usb ? m(pricing.USB_LIST_CENTS) : '');

    // The list subtotal and the saving are only meaningful on the pay-in-full
    // path, where there is a difference to show -- and, since 3 Sep 2026, only
    // when there is an order at all. Both conditions, one boolean; see the
    // unsellable note at the bottom of this block.
    //
    // THE SAVING IS list MINUS THE PAY-IN-FULL CHARGE. It is derived from
    // dueTodayCents(st, 'full') explicitly and never from `todayCents`, which is
    // whatever the CURRENT mode charges: on the deposit path `listCents -
    // todayCents` is 75% of the order, and it rendered as a "Pay-in-full saving"
    // of -$522 on a six-album deposit order until 24 Aug 2026. Deposit is the
    // default mode, so that was the first figure a visitor saw.
    //
    // This is NOT the `start_save` that order-builder.liquid computes for first
    // paint. That one is albums-only; the 10% mirror applies to photo sets and
    // the USB as well, so this figure spans all three components. They agree
    // only at first paint, where sets are 0 and the USB is off.
    //
    // The WRITES are gated as well as the rows. A row that is merely hidden
    // still holds its text, and hiding is one CSS rule away from failing -- that
    // is precisely how the -$522 reached the screen.
    //
    // AND ON THE UNSELLABLE STATE, since 3 Sep 2026. Both figures are literally
    // TRUE with nothing in the order -- the list subtotal really is $0 and the
    // pay-in-full saving really is $0 -- which is why they were left standing
    // when the per-album rate row, which was literally FALSE at zero, was gated
    // in the same pass. Literally true is not the bar on this card. Five
    // siblings above go away in this state so the card cannot half-agree with
    // itself, and a summary rail advertising a "Pay-in-full saving" of nothing,
    // beside a submit that will refuse the order, is noise on the page that
    // takes money. The row AND the write, as everywhere else here.
    //
    // `unsellable`, NOT `refusing`, and that is a decision rather than an
    // oversight. Past the self-serve ceiling these two stay with the three
    // sibling LINE rows above them, which are gated on their own counts alone:
    // an over-ceiling order still has a list value, and the card goes on
    // itemising what was configured while it offers a conversation instead of a
    // checkout. With nothing in the order there is nothing to itemise.
    var fullSavingCents = listCents - pricing.dueTodayCents(st, 'full');
    var showListSave = !isDeposit && !unsellable;
    show(el.sumListRow, showListSave);
    show(el.sumSaveRow, showListSave);
    text(el.sumList, showListSave ? m(listCents) : '');
    text(el.sumSave, showListSave ? '−' + m(fullSavingCents) : '');

    // ONE FIGURE, TWO SLOTS, ONE GATE. Both are written from `todayCents` -- the
    // same local the lines above use -- rather than recomputed, so there is no
    // second price path to keep in step; and both go EMPTY in either refusing
    // state, because a hidden slot still holds its text and the figure it would
    // hold is the one the page has just refused to honour. `text()` no-ops when
    // the merchant has turned the bar off.
    var dueFigure = refusing ? '' : m(todayCents);
    text(el.grand, dueFigure);
    text(el.barDue, dueFigure);
    text(el.duenote, refusing ? ''
      : (isDeposit ? balanceSentence(sec, balanceCents) : fullSentence(sec, fullSavingCents)));

    if (opts && opts.announce) {
      form.dispatchEvent(new CustomEvent('rf-ob:changed'));
    }
    // Stash the settled sentence so the debounced announcer does not have to
    // recompute state it would then be reading a tick late.
    //
    // In EITHER refusing state this is a sentence, not a price: `refusing` above
    // has already blanked the due-today row and the bar, and a live region that
    // goes on quoting a total for an order the page has just refused to sell
    // tells a screen-reader user the opposite of what the screen says. The
    // chooser is pure and lives below so it can be tested under node (D17).
    el.pending = liveSentence(st, {
      sentence: summarySentence(sec, st, todayCents, balanceCents, isDeposit, m),
      ceiling: attr(sec, 'data-rf-ob-live-ceiling', ''),
      // THE CARD'S OWN NOTE, read off the DOM here so the chooser below stays
      // pure. It is the fallback for a merchant who blanked the announcement
      // setting: the copy is already on the screen in that state, so the live
      // region can agree with it without this file holding any English.
      note: el.ceilingNote ? String(el.ceilingNote.textContent).trim() : '',
      // The same pair again for the unsellable state. Two keys and not one,
      // because the two states are on screen at different times: `note` is the
      // over-ceiling card's copy, which is HIDDEN when the order is empty, so
      // reusing it would announce "that is a big collection" about no albums.
      minimum: attr(sec, 'data-rf-ob-live-minimum', ''),
      minimumNote: el.minimumNote ? String(el.minimumNote.textContent).trim() : '',
      // THE ENTRY CAP'S COPY, READ OFF THE NODE THAT IS ON SCREEN -- the same
      // treatment `note` and `minimumNote` get, and the reason U4 needed no
      // second setting for the announcement. The words a screen-reader user
      // hears are the words beside the field, so the two cannot be edited into
      // disagreeing, and blanking the setting removes both at once instead of
      // leaving the region announcing a sentence nothing shows.
      cap: el.setsCapNote ? String(el.setsCapNote.textContent).trim() : ''
    // THE SAME `limits` THE SUBMIT GATE ABOVE WAS HIDDEN WITH. A third argument
    // rather than a key on the templates object, because that object is COPY --
    // strings resolved off the DOM -- and a number that decides a verdict does
    // not belong in it. The fourth is the clamp VERDICT and is kept out of the
    // templates object for exactly the same reason.
    }, limits, el.setsClamped === true);
  }

  /* Copy comes from data- attributes on the section so it stays merchant-editable
     and this file holds no English. Same mechanism rf-album-calculator.js uses
     for its tier and saving templates. */
  function attr(sec, name, fallback) {
    var v = sec.getAttribute ? sec.getAttribute(name) : null;
    return v === null || v === '' ? fallback : v;
  }

  // Same read, but an EMPTY attribute means empty rather than absent.
  //
  // attr() above cannot tell the two apart, which is right for a setting whose
  // copy is mandatory -- blanking it there should fall back to English rather
  // than render a bare figure with no words around it. It is wrong for a setting
  // whose schema says "leave blank to hide it": the merchant does exactly that,
  // and the hardcoded English reappears on every render. Two settings document
  // that promise, so two settings need this reader.
  function attrAllowBlank(sec, name, fallback) {
    if (!sec.hasAttribute) return fallback;
    return sec.hasAttribute(name) ? sec.getAttribute(name) : fallback;
  }

  function unitFor(sec, key, n) {
    var one = attr(sec, 'data-rf-ob-' + key + '-one', key === 'albums' ? 'album' : 'set');
    var many = attr(sec, 'data-rf-ob-' + key + '-many', key === 'albums' ? 'albums' : 'sets');
    return Number(n) === 1 ? one : many;
  }

  // The summary's wording for photo sets, which is deliberately NOT the stepper's.
  // Beside the field the unit noun reads "sets of 100"; in the summary the design
  // spells the multiplication out -- "3 sets x 100 loose photos". Two separate
  // strings for two separate jobs, so editing one cannot disturb the other.
  //
  // {count} follows the theme's existing placeholder convention rather than the
  // string concatenation this replaced -- see data-rf-tier-tpl in
  // sections/home-pricing-peek.liquid, read by rf-album-calculator.js. A merchant
  // who needs the number somewhere other than the front of the sentence can now
  // put it there; `n + ' ' + noun` could never express that.
  // The volume saving beside the per-album rate. savedPercent() is READ here,
  // never reimplemented: it is the same function the pricing page's calculator
  // uses (rf-album-calculator.js:91) and is pinned by tests/album-calculator.test.js,
  // so a second copy here would be free to drift from the page that quotes it.
  //
  // What it measures, so nobody has to re-derive it: the saving against the
  // SINGLE-ALBUM rate of $129 -- a volume claim, unrelated to the 10% pay-in-full
  // mirror. It reaches 39% at the floor rate.
  function rateSaveNote(sec, albums) {
    var pct = pricing.savedPercent(albums);
    if (pct > 0) {
      return fill(attrAllowBlank(sec, 'data-rf-ob-rate-save-tpl', ' · saving {percent}%'),
                  { '{percent}': String(pct) });
    }
    return attrAllowBlank(sec, 'data-rf-ob-rate-standard', ' · standard rate');
  }

  function setsSummaryLabel(sec, n) {
    var tpl = Number(n) === 1
      ? attr(sec, 'data-rf-ob-sum-sets-one', '{count} set × 100 loose photos')
      : attr(sec, 'data-rf-ob-sum-sets-many', '{count} sets × 100 loose photos');
    return fill(tpl, { '{count}': String(n) });
  }

  // One substitution helper for every merchant template on this page.
  //
  // split/join rather than String.replace, and not for style. With a plain-string
  // search pattern replace() still gives `$&` and `$'` special meaning INSIDE THE
  // REPLACEMENT -- verified: 'A{amount}B'.replace('{amount}', "$'") returns 'ABB'
  // -- and the replacement here is always a currency string. `$1` is NOT special
  // without a RegExp carrying capture groups; an earlier version of this comment
  // said it was, which was wrong. split/join has no such reading at all, and it
  // fills a token appearing twice, which replace() silently would not.
  //
  // Note rf-album-calculator.js has its own fill() with the same name and a
  // regex implementation. Both are IIFE-scoped so they cannot collide, but they
  // are not interchangeable -- do not copy one into the other's file.
  function fill(tpl, values) {
    var out = String(tpl);
    for (var k in values) {
      if (Object.prototype.hasOwnProperty.call(values, k)) {
        out = out.split(k).join(values[k]);
      }
    }
    return out;
  }

  // These four were assembled as `prefix + ' ' + money + suffix`, which put the
  // space, the colon, the comma and the full stop in this file where no merchant
  // could reach them -- and fixed the figure to the middle of the sentence. One
  // template per sentence puts the punctuation and the word order back in the
  // theme editor, where the rest of this page's copy already lives.
  function balanceSentence(sec, cents) {
    return fill(attr(sec, 'data-rf-ob-balance-tpl', 'Then {amount} when your images are ready.'),
                { '{amount}': pricing.moneyFromCents(cents) });
  }

  function fullSentence(sec, saved) {
    return fill(attr(sec, 'data-rf-ob-saved-tpl', 'You save {amount} by paying in full.'),
                { '{amount}': pricing.moneyFromCents(saved) });
  }

  function summarySentence(sec, st, todayCents, balanceCents, isDeposit, m) {
    // One complete, self-contained sentence per payment mode rather than a stem
    // plus an appended clause: a live region announcement has no surrounding
    // context to lean on. "Estimated" is an honest hedge covering tax and
    // shipping, which are added at checkout.
    // {balance} resolves to EMPTY on the pay-in-full path, which is what the
    // setting's own info text promises. balanceCents is computed unconditionally
    // in render(), so passing it through here regardless of mode would let a
    // merchant who adds {balance} to the pay-in-full template have a screen
    // reader announce a real dollar balance the customer does not owe. The old
    // stem-plus-clause code made that impossible by construction; one template
    // per sentence makes it reachable, so the guard has to be explicit.
    var tpl = isDeposit
      ? attr(sec, 'data-rf-ob-live-deposit', 'Estimated due today: {amount}, with a balance of {balance}.')
      : attr(sec, 'data-rf-ob-live-full', 'Estimated due today: {amount}.');
    return fill(tpl, {
      '{amount}': m(todayCents),
      '{balance}': isDeposit ? m(balanceCents) : ''
    });
  }

  /**
   * Is there anything in this order at all?
   *
   * THE ONE DEFINITION, and it is deliberately not "the subtotal is zero": the
   * minimum sellable order is >=1 album OR >=1 photo set, and a USB drive does
   * NOT count toward it. A cart holding only the USB is a $24.99 container for
   * images nobody is sending us, so it is refused rather than sold -- the same
   * call the self-serve ceiling makes at the other end of the range.
   *
   * Read through the module's own clamps rather than off the raw properties, so
   * a state object carrying '0', '' or nothing at all resolves the same way the
   * price does. That is not hypothetical: readState() in rf-order-cart.js
   * defaults an absent field to 0 precisely so a builder that failed to boot
   * lands here instead of quietly selling one album.
   *
   * PURE, and exported through the CommonJS hatch so the definition is pinned by
   * a test rather than by agreement between two files.
   */
  function isUnsellable(state) {
    var st = state || {};
    return pricing.orderAlbums(st.albums) === 0 && pricing.clampSets(st.sets) === 0;
  }

  /**
   * Which sentence the live region reads -- the price, one of two refusals, the
   * entry cap's message, or nothing.
   * PURE -- plain
   * data in, a string out -- so it is exported through the CommonJS hatch and
   * pinned by tests/order-builder.test.js and
   * tests/order-builder-strings.test.js rather than only by a browser.
   *
   * `state` is a readState() object; only the counts are read, and only through
   * pricing.exceedsSelfServe -- so the RULE, which is now two limits and not one
   * ceiling, is applied in exactly one place. It is the SAME call render() makes
   * to hide the submit, given the SAME `limits` object, so the two cannot
   * disagree about what is on screen. That second argument is why `limits` is
   * resolved once at the top of render() and passed down rather than re-read
   * here: two reads of a merchant setting are two chances to answer differently.
   *
   * `templates` carries strings the caller has already resolved from the
   * section's data- attributes -- resolving them here would put the DOM back
   * inside a function whose whole point is not to touch it.
   *
   * THE FALLBACK ORDER PAST THE CEILING, and it never ends at the price:
   *
   *   1. `templates.ceiling` -- the merchant's `live_region_ceiling` setting.
   *   2. `templates.note`    -- the text of the over-ceiling card itself
   *                             (`ceiling_note`), which is what the customer is
   *                             looking at in this state, read off the DOM by
   *                             render() under the rule above.
   *   3. the empty string    -- silence.
   *
   * IT USED TO FALL BACK TO `settled`, and that was a hole: `live_region_ceiling`
   * is a textarea in the theme editor, so blanking it re-enabled the exact defect
   * this helper exists to prevent and turned R2 -- "the live region never
   * announces a price past the self-serve ceiling" -- into a merchant preference.
   * A requirement a text field can switch off is not a requirement. Found by
   * adversarial review, 28 Aug 2026.
   *
   * Step 3 is deliberate and it is a real cost: a live region that goes quiet
   * tells a screen-reader user nothing happened. It is still the smaller harm.
   * Quoting a total for an order the page has just refused to sell tells them the
   * opposite of what the screen says, on the page that takes money -- and both
   * strings are blank only when a merchant has emptied two settings, while the
   * price is wrong here whatever they did. Inventing English at step 3 would put
   * copy back into this file (D2).
   *
   * Blankness is tested after trimming, on both strings: a setting holding a
   * single space announces nothing, so it has to be treated as the blank it is
   * rather than as copy. The string itself is returned untrimmed inside, exactly
   * as the merchant wrote it.
   *
   * THE FOURTH BRANCH, added 3 Sep 2026 with the zero-album floor, and it comes
   * FIRST. An empty order is UNDER the ceiling, so before this branch existed
   * the sellable early-return fired and the region announced "Estimated due
   * today: $0." beside a card that had blanked its due-today row and a submit
   * that refuses. That is the same defect this helper's own header calls the
   * opposite of what the screen says, on the page that takes money -- at $0
   * instead of $7,900. (The string really is "$0": moneyFromCents prints whole
   * dollars bare -- moneyFromCents(0) === '$0' -- which is if anything worse,
   * because it reads like a free order rather than like a missing figure.) Its chain mirrors the ceiling's exactly, and for the same
   * reason it must never end at the price:
   *
   *   1. `templates.minimum`     -- the merchant's `live_region_minimum` setting.
   *   2. `templates.minimumNote` -- the text of the summary card's own
   *                                 minimum-order note (`minimum_note`), which is
   *                                 what the customer is looking at in this
   *                                 state, read off the DOM by render().
   *   3. the empty string        -- silence.
   *
   * The two refusing states are mutually exclusive by arithmetic, not by luck:
   * exceedsSelfServe() needs ONE OF THE TWO COUNTS ABOVE ITS OWN LIMIT and this
   * one needs BOTH counts at 0, so no input satisfies both and the branch order
   * cannot hide a state. The argument survived the ceiling becoming two limits
   * and then becoming merchant-editable, and it survives for a reason that does
   * not depend on either value: every limit this theme can be given is at least
   * 1 -- `max_self_serve_albums` and `max_self_serve_sets` (Theme settings ->
   * Order rules) are ranges with a minimum of 1, and pricing.selfServeLimits()
   * sends anything below that back to the shipped default -- so a count ABOVE a
   * limit is a count above 0, and cannot be one of the two zeroes this branch
   * needs. It said "a count above 50" until 19 Sep 2026, which was a single
   * literal standing in for a rule that now has two editable halves.
   *
   * WHILE THE ALBUM MINIMUM IS ON THAT IS TRUE TWICE OVER, AND THE SECOND REASON
   * IS NOT ONE TO LEAN ON. `allow_zero_album_orders` off floors the album count
   * at 1, so isUnsellable() cannot fire at all and the two states are disjoint
   * for a reason that has nothing to do with arithmetic. That reason is a
   * checkbox rather than a deploy, and it disappears the moment a merchant
   * clears it. The arithmetic is the load-bearing half; do not simplify this
   * chain, or the branch order below it, on the strength of the other one.
   *
   * THE FIFTH BRANCH IS THE ENTRY CAP (U4), AND IT COMES LAST OF THE FOUR THAT
   * CAN SPEAK. `clamped` is the FOURTH ARGUMENT for the same reason `limits` is
   * the third: it is a verdict, and `templates` is copy. Its own chain is two
   * links rather than three --
   *
   *   1. `templates.cap` -- the text of the message beside the field
   *                         (`sets_cap_note`), read off the DOM by render().
   *   2. the settled PRICE.
   *
   * -- because there is no separate announcement setting to fall back FROM and
   * no reason to end in silence: the clamped count has a real price and the
   * card is showing it. That is the one structural difference from the two
   * refusing chains above, and it is deliberate. The full order is
   * unsellable -> over-ceiling -> clamped -> price; the branch bodies say why.
   *
   * ⚠ IT IS NOT MUTUALLY EXCLUSIVE WITH THE TWO ABOVE IT, unlike those two with
   * each other. A clamped photo-set count sits happily beside 51 albums, and on
   * SCREEN both are shown -- render() decided that and says why. This chooser is
   * the one place that has to rank them, because there is one region and one
   * sentence.
   */
  function liveSentence(state, templates, limits, clamped) {
    var t = templates || {};
    var settled = t.sentence == null ? '' : String(t.sentence);
    // Nothing in the order. Checked before the sellable return, because an empty
    // order is under the ceiling and would otherwise be announced as a price.
    if (isUnsellable(state)) {
      var minimum = t.minimum == null ? '' : String(t.minimum);
      if (minimum.trim() !== '') return minimum;
      var minNote = t.minimumNote == null ? '' : String(t.minimumNote);
      if (minNote.trim() !== '') return minNote;
      return '';
    }
    // Past the ceiling. Read from the same call render() makes to hide the
    // submit, so the two cannot disagree about what is on screen.
    //
    // ⚠ THIS USED TO BE THE EARLY RETURN -- `if (!exceedsSelfServe(...)) return
    // settled;` -- and it is now a positive branch because the sellable state
    // has a second thing it may have to say (below). The EXPRESSION is
    // unchanged and must stay spelled this way: it is pinned by name, with a
    // control arm, in tests/order-builder.test.js.
    if (pricing.exceedsSelfServe(state || {}, limits)) {
      var ceiling = t.ceiling == null ? '' : String(t.ceiling);
      if (ceiling.trim() !== '') return ceiling;
      var note = t.note == null ? '' : String(t.note);
      if (note.trim() !== '') return note;
      return '';
    }
    // THE ENTRY CAP, AND IT RANKS BELOW BOTH REFUSALS ON PURPOSE (U4).
    //
    // The screen shows the clamp message and the over-ceiling card together --
    // render() says why -- but the region is ONE sentence and has to choose.
    // The two states above are the page refusing to sell the order at all; this
    // one is a correction inside an order it is still willing to sell, so the
    // refusal is the more urgent thing to hear and it is the one the screen's
    // loudest element carries. The chain ends at the PRICE rather than at
    // silence, unlike the two above it, because there is a real price here and
    // it is honest: the figure on the card is the figure for the clamped count.
    //
    // ⚠ IT OUTRANKS THE PRICE, AND THAT COSTS SOMETHING WORTH NAMING. The flag
    // stands until the customer commits the count again, so while the message
    // is on screen an unrelated change -- switching payment mode, adding the USB
    // -- announces the cap again instead of the new total. That is the same
    // trade the ceiling branch above already makes, and it is the right way
    // round here: a customer who asked for 400 sets and hears only "$725 due
    // today" has been told the price of an order they did not configure.
    //
    // Blankness is tested after trimming, like every other string in this
    // chooser: a setting holding a single space announces nothing, and a
    // merchant who blanked `sets_cap_note` renders no message at all -- so the
    // region falls through to the price rather than going quiet about a screen
    // that shows nothing new.
    if (clamped === true) {
      var cap = t.cap == null ? '' : String(t.cap);
      if (cap.trim() !== '') return cap;
    }
    // Under the ceiling, unclamped: this is the sellable state and the price IS
    // the announcement.
    return settled;
  }

  /**
   * Write the settled sentence into the live region.
   *
   * "NO LIVE REGION" AND "NOTHING TO SAY" ARE DIFFERENT ANSWERS, and conflating
   * them is what this reads as one condition until 3 Sep 2026:
   *
   *     if (!el.live || !el.pending) return;
   *
   * `!el.pending` is true of the empty string, so liveSentence()'s step 3 --
   * SILENCE, the deliberate end of both fallback chains -- skipped the write and
   * left the region holding whatever it last held. On the path that matters that
   * is a PRICE: a customer steps albums to 0, the card blanks its due-today row,
   * the submit will refuse the order, and a screen reader is still holding
   * "Estimated due today: $626.40" from the render before. The header above
   * liveSentence() calls quoting a total for a refused order "the opposite of what
   * the screen says"; not clearing it says exactly that, one render late, and it
   * is the state the whole fallback chain exists to reach.
   *
   * So the two are separated. `el.live` absent means there is nothing to write to
   * -- a merchant layout without the region, or a rebind mid-flight. `el.pending`
   * absent (`undefined`, before the first render has stashed one) means there is
   * nothing to say YET, which is also a skip. An empty STRING is a value: it is
   * the chooser's considered answer and it gets written, clearing the region.
   */
  function announce(el) {
    // The 400ms timer belongs to a closure the rebind cannot reach, so a
    // shopify:section:load inside that window leaves it queued against the OLD
    // live region. Checking connectedness is cheaper than a teardown API and
    // makes the stale timer harmless whenever it lands.
    if (el.live && el.live.isConnected === false) return;
    if (!el.live) return;
    // `== null`, so it catches undefined as well, and NOT `!el.pending` -- see
    // the note above. Loose equality is the whole point of the line.
    if (el.pending == null) return;
    // Replace textContent. Never toggle aria-hidden to clear a live region:
    // taking it out of the accessibility tree and putting it back races the
    // assistive technology's own change detection.
    el.live.textContent = el.pending;
  }

  function boot() {
    try {
      if (!pricing) throw new Error('rf-pricing.js did not load before rf-order-builder.js');
      setup(document);
    } catch (e) {
      // Degrade to exactly the state JavaScript-disabled produces, rather than to
      // a dead submit button. The noscript stylesheet cannot help here, because
      // JavaScript did run -- it just failed.
      [].slice.call(document.querySelectorAll('[data-rf-ob-nojs]')).forEach(function (n) {
        n.removeAttribute('hidden');
      });
      [].slice.call(document.querySelectorAll('[data-rf-ob-submit-wrap]')).forEach(function (n) {
        n.setAttribute('hidden', '');
      });
      console.error(e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor reloads a single section without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    try {
      setup(e.target);
    } catch (err) {
      console.error(err);
    }
  });
})();
